import Model from 'src/app/common/model';

/**
 * @model DeviceModel
 * @description
 * Represents the catalog definition of a hardware device model within the AquaCloud application.
 * Unlike `Device` (which represents a physical asset with a serial number) or `CustomerDeviceModel`
 * (which represents a deployed asset), `DeviceModel` defines the *type* or *class* of the hardware.
 *
 * **Domain Context:**
 * Hardware manufacturers produce various versions and types of equipment (e.g., "Sensor Node V1",
 * "Sensor Node V2", "Automatic Feeder"). This model serves as the master record for these types.
 *
 * **Purpose:**
 * To provide a standardized vocabulary for device types. This allows the system to:
 * 1. Group devices by their capabilities.
 * 2. Apply specific firmware or configuration rules based on the model.
 * 3. Filter inventory and reports.
 *
 * **Key Responsibilities:**
 * 1. **Identification**: Stores the official commercial `name` of the hardware model.
 * 2. **Abbreviation**: Provides a short code (`abbreviation`) for compact UI elements or internal logic.
 * 3. **Documentation**: Holds a `description` of the model's features or intended use.
 *
 * **Data Flow:**
 * - **Configuration**: System Administrators define these models in the Master Data section.
 * - **Usage**:
 *   - **Inventory**: When adding a new device, the user selects its `DeviceModel`.
 *   - **Filtering**: Users filter device lists by model type.
 *
 * **User Roles & Permissions:**
 * - **System Admin**: Can create, update, or delete device models.
 * - **Inventory Manager**: Uses these definitions to categorize stock.
 *
 * **Features:**
 * - Lightweight reference entity.
 * - Extends the base `Model` for standard ID and timestamp handling.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class DeviceModel extends Model {
  /**
   * @type {string}
   * @description
   * The commercial or display name of the device model.
   * @example "AquaSense Pro", "BluePod Mini"
   */
  name: string;

  /**
   * @type {string}
   * @description
   * A short code or acronym representing the device model.
   * Useful for generating serial number prefixes or displaying in dense tables.
   * @optional
   * @example "ASP", "BPM"
   */
  abbreviation?: string;

  /**
   * @type {string}
   * @description
   * A detailed description of the device model, including capabilities or version notes.
   * @optional
   */
  description?: string;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the DeviceModel class.
   * Calls the parent `Model` constructor to initialize base properties.
   */
  constructor() {
    super();
  }
}
